import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Stack from 'react-bootstrap/Stack';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Carousel from 'react-multi-carousel';
import ReactPaginate from 'react-paginate';
import Button from 'react-bootstrap/Button';
import AnimateHeight from 'react-animate-height';
import { useSelector } from 'react-redux';
import EmptyResult from '../EmptyResult';
import PostBlok from './PostBlok';
import useWindowSize from '../../hooks/useWindowSize';
import i18next from '../../translations/i18n';

import 'react-multi-carousel/lib/styles.css';

import './index.scss';
import API from '../../services/API';

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    partialVisibilityGutter: 50,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
    partialVisibilityGutter: 50,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    partialVisibilityGutter: 50,
  },
};

const BlogPosts = ({ postsToPublish }) => {
  const size = useWindowSize();
  const pageTopRef = useRef(null);
  const [grid, setGrid] = useState(postsToPublish);
  const [itemOffset, setItemOffset] = useState(0);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = size.width >= 1710 ? 8 : 6;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = grid.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(Object.keys(grid).length / itemsPerPage);
  const calculatePercentage = (index) => Math.abs(Math.round(((index + 1) / postsToPublish.length) * 100));
  const [percentage, setPercentage] = useState(calculatePercentage(0));
  const assetClasses = useSelector(state => state.app);
  const { filters, filteredPosts = [] } = assetClasses;
  const updatedFilters = filters.filter(f => f.checked).map(filter => filter.name).join(', ');

  const getFeature = (posts) =>
    posts.map((postObj) => <PostBlok post={postObj} key={postObj._uid} />);

  // Invoke when user click to request another page.
  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % postsToPublish.length;
    setItemOffset(newOffset);
    setCurrentPage(event.selected);
    pageTopRef.current.scrollIntoView();
  };

  useEffect(() => {
    const init = async () => {
      if (filteredPosts.length > 0 || updatedFilters) {
        if (filteredPosts.slice(itemOffset, endOffset).length <= 0) {
          setItemOffset(0);
          setCurrentPage(0);
        }

        setGrid(filteredPosts);
      } else {
        setGrid(postsToPublish);
      }
    };

    init();
  }, [size, postsToPublish, itemOffset, filteredPosts]);

  return (
    <div>
      {/*  Mobiles only */}
      <div className="d-block d-md-none post">
        {currentItems.length > 0 && (
          <>
            <Carousel
              rewind
              showDots={false}
              keyBoardControl
              partialVisible
              itemClass="image-item"
              responsive={responsive}
              afterChange={() => {}}
              beforeChange={(event) => {
                setPercentage(calculatePercentage(event));
              }}
            >
              {getFeature(filteredPosts.length > 0 || updatedFilters ? filteredPosts : postsToPublish)}
            </Carousel>
            <Stack className="mt-5">
              <ProgressBar now={percentage} variant="info" className="p-0 mx-3" />
            </Stack>
          </>
        )}
        {currentItems.length === 0 && <EmptyResult checkedFilters={updatedFilters} message={i18next.t('NoResultFoundMsg')} />}
      </div>

      {/* Medium and desktop devices */}
      <div className="desktop d-none d-md-block post" ref={pageTopRef}>
        {currentItems.length > 0 && (
        <AnimateHeight
          duration={500}
          height={open ? 'auto' : 600}
          onHeightAnimationEnd={() => true}
        >
          <div id="collapse-text">
            <Row className="g-2">
              {currentItems.map(col => <PostBlok post={col} key={`${col._uid}`} />)}
            </Row>

            <section className="mt-3 d-flex justify-content-center ">
              <Row className="m-0">
                <Col style={{ zIndex: 1 }}>
                  <ReactPaginate
                    breakLabel="..."
                    nextLabel="&raquo;"
                    onPageChange={handlePageClick}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={3}
                    pageCount={pageCount}
                    previousLabel="&laquo;"
                    renderOnZeroPageCount={null}
                    pageClassName="page-item"
                    pageLinkClassName="page-link fw-bold"
                    previousClassName="page-item shadow-lg"
                    previousLinkClassName="page-link fw-bold"
                    nextClassName="page-item"
                    nextLinkClassName="page-link fw-bold"
                    breakClassName="page-item"
                    breakLinkClassName="page-link fw-bold"
                    containerClassName="pagination"
                    activeClassName="active"
                    forcePage={currentPage}
                  />
                </Col>
              </Row>
            </section>
          </div>
        </AnimateHeight>
        )}
        {currentItems.length === 0 && <EmptyResult checkedFilters={updatedFilters} message={i18next.t('NoResultFoundMsg')} />}
        {currentItems.length > 0 && (
        <Stack className={`justify-content-center d-flex post__slider-close hstack ${open ? 'post__slider-open' : ''}`}>
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              API.sendToGA(
                `${open ? i18next.t('seeLess') : i18next.t('seeMore')}`,
              );
              pageTopRef.current.scrollIntoView();
              setOpen(!open);
            }}
            aria-expanded={open}
            variant="outline-primary px-4 content__slider-btn"
            size="lg"
            className={!open ? 'view-btn' : ''}
            title={!open ? `${i18next.t('seeMore')}` : ''}
          >
            {open ? i18next.t('seeLess') : i18next.t('seeMore')}
          </Button>
        </Stack>
        )}
      </div>
    </div>
  );
};
BlogPosts.defaultProps = {
  postsToPublish: [],
};
BlogPosts.propTypes = {
  postsToPublish: PropTypes.arrayOf(PropTypes.shape([])),
};

export default BlogPosts;
